import type { FC, ReactNode } from "react";

import { EyebrowLabel } from "./EyebrowLabel";

export interface PanelHeaderBlockProps {
  /**
   * Small uppercase label above the title
   */
  eyebrow?: string;

  /**
   * Main title text
   */
  title: string;

  /**
   * Description text below the title
   */
  description?: string;

  /**
   * Optional badge or icon to show next to title
   */
  badge?: ReactNode;

  /**
   * Additional CSS classes
   */
  className?: string;
}

/**
 * PanelHeaderBlock Component
 *
 * A reusable header pattern for panels with eyebrow label, title, and description.
 *
 * @example
 * ```tsx
 * <PanelSection
 *   headerContent={
 *     <PanelHeaderBlock
 *       eyebrow="Repository Indexing"
 *       title="Smart Run Activity"
 *       description="Review recent repository indexing runs."
 *     />
 *   }
 * >
 *   Panel content
 * </PanelSection>
 * ```
 */
export const PanelHeaderBlock: FC<PanelHeaderBlockProps> = ({
  eyebrow,
  title,
  description,
  badge,
  className = "",
}) => (
  <div className={`flex flex-col gap-0.5 ${className}`.trim()}>
    {eyebrow && <EyebrowLabel>{eyebrow}</EyebrowLabel>}
    <div className="flex items-center gap-2">
      <p className="font-mono text-[11px] font-bold uppercase tracking-widest text-gray-900 dark:text-white">
        {title}
      </p>
      {badge}
    </div>
    {description && (
      <p className="text-sm text-gray-500 dark:text-white/60">{description}</p>
    )}
  </div>
);
